import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { StoreLayout } from "../../components/StoreLayout";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  CalendarBlank, Clock, MapPin, NavigationArrow, CheckCircle, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);
const dayLabel = (isoStr) => new Date(isoStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

export default function SchedulePage() {
  const [params] = useSearchParams();
  const orderId = params.get("id") || "";
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [slotCfg, setSlotCfg] = useState({ slots: [], dates: [], min_date: "", max_date: "", availability: {} });
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [maps, setMaps] = useState("");
  const [remarks, setRemarks] = useState("");
  const [geoBusy, setGeoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!orderId) { setNotFound(true); return; }
    api.get(`/public/schedule/${encodeURIComponent(orderId)}`).then((r) => {
      if (r.data.found) {
        setOrder(r.data);
        if (r.data.preferred_service_date) setDate(r.data.preferred_service_date);
        if (r.data.preferred_slot) setSlot(r.data.preferred_slot);
        if (r.data.maps_location) setMaps(r.data.maps_location);
        if (r.data.customer_remarks) setRemarks(r.data.customer_remarks);
      } else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [orderId]);

  // Reuse existing Booking Slots logic (respects lead/max days & capacity)
  useEffect(() => {
    if (!order) return;
    const p = {};
    if (order.city) p.city = order.city;
    if (order.pincode) p.pincode = order.pincode;
    if (date) p.date = date;
    api.get("/public/booking-slots", { params: p }).then((r) => {
      setSlotCfg(r.data);
      if (!date && r.data.min_date) setDate(r.data.min_date);
    }).catch(() => {});
  }, [order, date]);

  useEffect(() => { setSlot((s) => (slotCfg.slots || []).includes(s) ? s : ""); }, [date]); // eslint-disable-line

  const holidays = order?.holidays || [];
  const isHoliday = (d) => holidays.includes(d);

  const shareLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setMaps(`https://www.google.com/maps?q=${latitude},${longitude}`);
        toast.success("Location captured");
        setGeoBusy(false);
      },
      () => { toast.error("Couldn't get your location. Please paste a Google Maps link instead."); setGeoBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 });
  };

  const submit = async () => {
    if (!date) { toast.error("Please pick a preferred date"); return; }
    if (date < todayIso()) { toast.error("Please choose a future date"); return; }
    if (isHoliday(date)) { toast.error("That date is not available. Please pick another day."); return; }
    if (!slot) { toast.error("Please pick a time slot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/schedule/${encodeURIComponent(orderId)}/submit`, {
        preferred_service_date: date, preferred_slot: slot,
        customer_remarks: remarks, maps_location: maps,
      });
      setDone(data.message || true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <StoreLayout>
        <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="schedule-not-found">
          <h1 className="font-[Outfit] text-2xl font-extrabold">Order not found</h1>
          <p className="text-[#475569] mt-3">We couldn't find your order. Please check the scheduling link from your WhatsApp message and try again.</p>
        </div>
      </StoreLayout>
    );
  }

  if (done) {
    return (
      <StoreLayout>
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="schedule-done">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle size={44} weight="fill" className="text-green-600" />
          </div>
          <h1 className="font-[Outfit] text-3xl font-extrabold tracking-tight mt-6">Schedule Received</h1>
          <p className="text-[#475569] mt-3">{typeof done === "string" ? done : "Thank you. Your preferred schedule has been received. Our team will confirm your appointment shortly."}</p>
          <Button className="rounded-xl mt-8 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold" onClick={() => (window.location.href = "/")}>Back to Home</Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="bg-[#1E293B] text-white">
        <div className="max-w-3xl mx-auto px-5 py-12">
          <div className="flex items-center gap-2 text-[#C7D2FE]"><ShieldCheck size={22} weight="fill" /><span className="uppercase text-xs tracking-[0.2em] font-semibold">Fixigo Scheduling</span></div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Choose your preferred appointment</h1>
          <p className="text-white/70 mt-3 max-w-2xl">Tell us when it's convenient and where to reach you. Our team will confirm your appointment shortly.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {order && (
          <div className="bg-[#eef2ff] border border-[#c7d2fe] rounded-2xl p-4" data-testid="schedule-order-info">
            <p className="text-xs uppercase tracking-wide text-[#4F46E5] font-bold">Your order</p>
            <p className="font-[Outfit] font-bold mt-1">{order.service_type} · {order.product_category}</p>
            <p className="text-sm text-[#475569]">{order.customer_name}
              <span className="ml-1">· Order No: <strong className="text-[#1E293B]">{order.order_id}</strong></span>
              {order.is_b2b && order.b2b_partner_name ? ` · ${order.b2b_partner_name}` : ""}
            </p>
          </div>
        )}

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-6">
          <div>
            <Label className="label-caps flex items-center gap-1.5"><CalendarBlank size={14} /> Preferred Service Date</Label>
            <Input type="date" value={date} min={slotCfg.min_date || todayIso()} max={slotCfg.max_date || undefined}
              onChange={(e) => setDate(e.target.value)} className="rounded-lg mt-1.5 max-w-[220px]" data-testid="schedule-date" />
            {date && isHoliday(date) && <p className="text-xs text-red-600 mt-1">This date is not available. Please pick another day.</p>}
          </div>

          <div>
            <Label className="label-caps flex items-center gap-1.5"><Clock size={14} /> Preferred Time Slot</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="schedule-slot-grid">
              {(slotCfg.slots || []).map((s) => {
                const av = (slotCfg.availability || {})[s];
                const full = av?.full;
                if (full && slotCfg.partner_availability) return null; // Phase 2: hide unavailable slots
                return (
                  <button key={s} type="button" disabled={full} onClick={() => setSlot(s)} data-testid={`schedule-slot-${s.replace(/[^0-9]/g, "").slice(0, 4)}`}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${slot === s ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "border-[#E2E8F0] hover:border-[#4F46E5]"}`}>
                    {s}{full ? " · Full" : (av?.remaining != null ? ` · ${av.remaining} left` : "")}
                  </button>
                );
              })}
              {(() => {
                const visible = (slotCfg.slots || []).filter((s) => !((slotCfg.availability || {})[s]?.full && slotCfg.partner_availability));
                return visible.length === 0 ? <p className="text-xs text-[#94a3b8] col-span-full" data-testid="schedule-no-slots">No slots available for the selected day. Please choose another date.</p> : null;
              })()}
            </div>
          </div>

          <div>
            <Label className="label-caps flex items-center gap-1.5"><MapPin size={14} /> Service Location (Google Maps)</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
              <Input value={maps} onChange={(e) => setMaps(e.target.value)} placeholder="Paste your Google Maps link"
                className="rounded-lg flex-1" data-testid="schedule-maps" />
              <Button type="button" variant="outline" className="rounded-lg gap-1.5 shrink-0" onClick={shareLocation} disabled={geoBusy} data-testid="schedule-share-location">
                <NavigationArrow size={15} weight="bold" /> {geoBusy ? "Locating…" : "Share my location"}
              </Button>
            </div>
            {maps && <a href={maps} target="_blank" rel="noreferrer" className="text-xs text-[#4F46E5] font-semibold hover:underline mt-1 inline-block" data-testid="schedule-maps-preview">Open location ↗</a>}
          </div>

          <div>
            <Label className="label-caps">Remarks (optional)</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="rounded-lg mt-1.5"
              placeholder="Landmark, entry instructions, parking, alternate contact name/number, etc." data-testid="schedule-remarks" />
          </div>

          <Button className="rounded-xl w-full h-11 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold gap-2" onClick={submit} disabled={busy} data-testid="schedule-submit">
            <CheckCircle size={18} weight="bold" /> {busy ? "Submitting…" : "Submit Preferred Schedule"}
          </Button>
        </div>
      </div>
    </StoreLayout>
  );
}
